import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button, Input, Alert } from "@repo/ui";
import PhotoIcon from "../../../../assets/photo_icon.svg?react";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux/store";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import { registrationSteps } from "../../constants/steps";
import withAuth from "../../../../hoc/withAuth/withAuth";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import {
  updateGiftTableState,
  type Organizer as OrganizerState,
} from "../../../../redux/slices/giftTableSlice";
import {
  getPhoto,
  setPhoto,
} from "../../../../utils/indexedDB";
import {
  checkGiftTableSlug,
} from "../../../../services/giftTableApiService";
import { gsap } from "gsap";
import { FiPlus } from "react-icons/fi";
import * as Sentry from "@sentry/react";
import NavigationButtons from "../../components/NavigationButtons";

const LOCALE =
  "modules_Registration_pages_CreateGiftTable_CreateGiftTableStep2";

type OrganizerForm = OrganizerState;
type NormalizedLanguage = "es" | "en";

const MINIMUM_ORGANIZERS: Record<string, number> = {
  wedding: 2,
};

const EVENT_NAME_PREFIXES: Record<
  string,
  { es: string; en: string }
> = {
  wedding: { es: "Boda de", en: "Wedding of" },
  quinceanera: { es: "Quinceañera de", en: "Quinceañera for" },
  birthday: { es: "Cumpleaños de", en: "Birthday for" },
  baptism: { es: "Bautizo de", en: "Baptism of" },
  farewell: { es: "Despedida de soltera de", en: "Bachelorette party for" },
  default: { es: "Celebración de", en: "Celebration for" },
};

const CONNECTOR: Record<NormalizedLanguage, string> = {
  es: "y",
  en: "and",
};

const normalizeLanguage = (language?: string): NormalizedLanguage =>
  language?.startsWith("en") ? "en" : "es";

const createDefaultOrganizers = (eventType: string): OrganizerForm[] => {
  const minimum = MINIMUM_ORGANIZERS[eventType] || 1;
  return Array.from({ length: minimum }, () => ({ firstName: "", lastName: "" }));
};

const slugify = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const generateSlugSuggestion = (
  eventType: string,
  organizers: OrganizerForm[]
): string => {
  const baseParts = [
    eventType || "celebracion",
    ...organizers.map((organizer) => organizer.firstName.trim()),
  ].filter(Boolean);
  return slugify(baseParts.join("-"));
};

const joinNames = (
  names: string[],
  language: NormalizedLanguage
): string => {
  if (names.length === 0) {
    return "";
  }
  if (names.length === 1) {
    return names[0];
  }
  const connector = ` ${CONNECTOR[language]} `;
  return `${names.slice(0, -1).join(", ")}${connector}${names.slice(-1)}`;
};

const buildEventName = (
  eventType: string,
  organizers: OrganizerForm[],
  language: NormalizedLanguage
): string => {
  const prefix =
    EVENT_NAME_PREFIXES[eventType]?.[language] ??
    EVENT_NAME_PREFIXES.default[language];

  const firstNames = organizers
    .map((organizer) => organizer.firstName.trim())
    .filter(Boolean);
  if (!firstNames.length) {
    return "";
  }
  return `${prefix} ${joinNames(firstNames, language)}`;
};

const buildAmphitryon = (
  organizers: OrganizerForm[],
  language: NormalizedLanguage
): string => {
  const firstNames = organizers
    .map((organizer) => organizer.firstName.trim())
    .filter(Boolean);
  return joinNames(firstNames, language);
};

const CreateGiftTableStep2: React.FC = () => {
  const { t, i18n } = useTranslation(LOCALE);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const giftTable = useSelector((state: RootState) => state.giftTable);
  const savedLanguage = useSelector(
    (state: RootState) => state.auth.user?.language
  );
  const [eventName, setEventName] = useState(giftTable.eventName || "");
  const [eventDate, setEventDate] = useState(giftTable.eventDate || "");
  const [amphitryon, setAmphitryon] = useState(giftTable.amphitryon || "");
  const [photo, setPhotoState] = useState<string | null>(null);
  const [organizers, setOrganizers] = useState<OrganizerForm[]>(
    giftTable.organizers.length
      ? giftTable.organizers
      : createDefaultOrganizers(giftTable.eventType)
  );
  const [slug, setSlug] = useState(giftTable.slug || "");
  const [slugTouched, setSlugTouched] = useState(Boolean(giftTable.slug));
  const [slugStatus, setSlugStatus] = useState<
    "idle" | "checking" | "available" | "unavailable" | "invalid"
  >("idle");
  const [slugError, setSlugError] = useState<string | null>(null);
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: "info" | "warn" | "error" | "success";
  } | null>(null);

  const alertRef = useRef<HTMLDivElement>(null);
  const normalizedLanguage = useMemo(
    () => normalizeLanguage(savedLanguage || i18n.language),
    [savedLanguage, i18n.language]
  );
  const minimumOrganizers = MINIMUM_ORGANIZERS[giftTable.eventType] || 1;

  useEffect(() => {
    if (alertRef.current) {
      gsap.fromTo(
        alertRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 }
      );
    }
  }, [alert]);

  useEffect(() => {
    (async () => {
      const savedPhoto = await getPhoto("giftTablePhoto");
      if (savedPhoto) {
        setPhotoState(savedPhoto);
      }
    })();
  }, []);

  useEffect(() => {
    setEventName(giftTable.eventName);
    setEventDate(giftTable.eventDate);
    setAmphitryon(giftTable.amphitryon);
    if (giftTable.organizers.length) {
      setOrganizers(giftTable.organizers);
    }
    if (giftTable.slug) {
      setSlug(giftTable.slug);
      setSlugTouched(true);
    }
  }, [giftTable]);

  useEffect(() => {
    setOrganizers((current) => {
      if (current.length >= minimumOrganizers) {
        return current;
      }
      const missing = minimumOrganizers - current.length;
      return [
        ...current,
        ...Array.from({ length: missing }, () => ({
          firstName: "",
          lastName: "",
        })),
      ];
    });
  }, [minimumOrganizers]);

  useEffect(() => {
    const generatedEventName = buildEventName(
      giftTable.eventType,
      organizers,
      normalizedLanguage
    );
    if (generatedEventName) {
      setEventName(generatedEventName);
    } else if (!giftTable.eventName) {
      setEventName("");
    }

    const generatedAmphitryon = buildAmphitryon(
      organizers,
      normalizedLanguage
    );
    if (generatedAmphitryon) {
      setAmphitryon(generatedAmphitryon);
    } else if (!giftTable.amphitryon) {
      setAmphitryon("");
    }
  }, [giftTable.eventType, organizers, normalizedLanguage, giftTable]);

  useEffect(() => {
    if (slugTouched) {
      return;
    }
    const suggestion = generateSlugSuggestion(
      giftTable.eventType,
      organizers
    );
    if (suggestion && suggestion !== slug) {
      setSlug(suggestion);
    }
  }, [giftTable.eventType, organizers, slug, slugTouched]);

  useEffect(() => {
    if (!slug) {
      setSlugStatus("idle");
      setSlugError(null);
      return;
    }

    let isCurrent = true;
    setSlugStatus("checking");
    setSlugError(null);
    const timeoutId = setTimeout(() => {
      checkGiftTableSlug(slug, giftTable.ID || undefined)
        .then(({ available }) => {
          if (!isCurrent) {
            return;
          }
          if (available) {
            setSlugStatus("available");
            setSlugError(null);
          } else {
            setSlugStatus("unavailable");
            setSlugError(
              t("slugInUse", {
                defaultValue: "Este link ya está en uso.",
              })
            );
          }
        })
        .catch((error) => {
          if (!isCurrent) {
            return;
          }
          if ((error as Error).message === "INVALID_SLUG") {
            setSlugStatus("invalid");
            setSlugError(
              t("slugInvalid", {
                defaultValue:
                  "Solo puedes usar letras, números y guiones medios.",
              })
            );
          } else {
            setSlugStatus("invalid");
            setSlugError(
              t("slugValidationError", {
                defaultValue:
                  "No pudimos validar el link personalizado. Intenta de nuevo.",
              })
            );
          }
        });
    }, 400);

    return () => {
      isCurrent = false;
      clearTimeout(timeoutId);
    };
  }, [slug, giftTable.ID, t]);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          try {
            const result = reader.result as string;
            setPhotoState(result);
            await setPhoto("giftTablePhoto", result);
          } catch (error) {
            Sentry.captureException(error);
          }
        };
        reader.onerror = (error) => {
          Sentry.captureException(error);
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleOrganizerChange = (
    index: number,
    field: keyof OrganizerForm,
    value: string
  ) => {
    setOrganizers((current) => {
      const updated = [...current];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return updated;
    });
  };

  const handleAddOrganizer = () => {
    setOrganizers((current) => [
      ...current,
      { firstName: "", lastName: "" },
    ]);
  };

  const handleRemoveOrganizer = (index: number) => {
    setOrganizers((current) => {
      if (current.length <= minimumOrganizers) {
        return current;
      }
      return current.filter((_, idx) => idx !== index);
    });
  };

  const isOrganizerValid = (organizer: OrganizerForm) =>
    organizer.firstName.trim() !== "" && organizer.lastName.trim() !== "";

  const handleBack = () => {
    navigate("/registration/create-gift-table/step1");
  };

  const handleContinue = async () => {
    if (!photo || !eventDate) {
      setAlert({
        message: t("completeAllFieldsError"),
        level: "warn",
      });
      return;
    }

    if (
      organizers.some((organizer) => !isOrganizerValid(organizer))
    ) {
      setAlert({
        message: t("organizersError", {
          defaultValue: "Completa nombre y apellido de cada organizador.",
        }),
        level: "warn",
      });
      return;
    }

    if (!slug) {
      setAlert({
        message: t("slugRequiredError", {
          defaultValue: "Agrega un link personalizado para tu mesa.",
        }),
        level: "warn",
      });
      return;
    }

    try {
      const { available } = await checkGiftTableSlug(
        slug,
        giftTable.ID || undefined
      );
      if (!available) {
        setAlert({
          message: t("slugInUse", {
            defaultValue: "Este link ya está en uso. Intenta con otro.",
          }),
          level: "warn",
        });
        setSlugStatus("unavailable");
        return;
      }
    } catch (error) {
      if ((error as Error).message === "INVALID_SLUG") {
        setAlert({
          message: t("slugInvalid", {
            defaultValue:
              "Tu link solo puede incluir letras, números y guiones medios.",
          }),
          level: "warn",
        });
      } else {
        setAlert({
          message: t("slugValidationError", {
            defaultValue:
              "No pudimos validar tu link personalizado. Intenta nuevamente.",
          }),
          level: "error",
        });
      }
      setSlugStatus("invalid");
      return;
    }

    dispatch(
      updateGiftTableState({
        eventName,
        eventDate,
        amphitryon,
        organizers,
        slug,
      })
    );
    navigate("/registration/create-gift-table/step3");
  };

  const slugHelperText = useMemo(() => {
    if (!slug) {
      return t("slugHelper", {
        defaultValue:
          "Usa letras, números y guiones medios para tu link personalizado.",
      });
    }
    if (slugStatus === "checking") {
      return t("slugChecking", {
        defaultValue: "Verificando disponibilidad...",
      });
    }
    if (slugStatus === "available") {
      return t("slugAvailable", {
        defaultValue: "¡Disponible!",
      });
    }
    return slugError;
  }, [slug, slugStatus, slugError, t]);

  return (
    <PageContainer>
      <StepIndicator
        steps={registrationSteps}
        currentStep={3}
        progressPercentage={40}
      />
      <AnimatedCard title={t("title")} className="bg-white max-w-lg w-full">
        {alert && (
          <div ref={alertRef}>
            <Alert message={alert.message} level={alert.level} />
          </div>
        )}
        <div className="flex flex-col space-y-10">
          <div className="flex justify-center">
            <label
              htmlFor="photo-upload"
              className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer"
            >
              {photo ? (
                <img
                  src={photo}
                  alt="Event"
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <PhotoIcon />
              )}
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoUpload}
              />
            </label>
          </div>
          <p className="text-center">{t("photoLabel")}</p>
          <div className="flex flex-col space-y-4">
            <label htmlFor="eventName" className="text-label font-paragraph">
              {t("eventNameLabel")}
            </label>
            <Input
              value={eventName}
              readOnly
              disabled
              type="text"
              className="input-border"
            />
            <label htmlFor="amphitryon" className="text-label font-paragraph">
              {t("amphitryonLabel")}
            </label>
            <Input
              value={amphitryon}
              readOnly
              disabled
              type="text"
              placeholder={t("amphitryonPlaceholder")}
              className="input-border"
            />
            <label htmlFor="eventDate" className="text-label font-paragraph">
              {t("eventDateLabel")}
            </label>
            <Input
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              type="date"
              className="input-border"
            />
          </div>
          <div className="space-y-4">
            <p className="text-label font-paragraph">
              {t("organizersTitle", { defaultValue: "Organizadores" })}
            </p>
            <div className="space-y-4">
              {organizers.map((organizer, index) => {
                const isWedding = giftTable.eventType === "wedding";
                const title =
                  isWedding && index === 0
                    ? t("groomLabel", {
                        defaultValue: "Datos del novio",
                      })
                    : isWedding && index === 1
                      ? t("brideLabel", {
                          defaultValue: "Datos de la novia",
                        })
                      : t("organizerLabel", {
                          index: index + 1,
                          defaultValue: `Organizador ${index + 1}`,
                        });
                return (
                  <div
                    key={`organizer-${index}`}
                    className="rounded-xl border border-gray-200 p-4 space-y-3"
                  >
                    <p className="font-semibold">{title}</p>
                    <Input
                      value={organizer.firstName}
                      onChange={(e) =>
                        handleOrganizerChange(
                          index,
                          "firstName",
                          e.target.value
                        )
                      }
                      type="text"
                      placeholder={t("organizerFirstName", {
                        defaultValue: "Nombre(s)",
                      })}
                      className="input-border"
                    />
                    <Input
                      value={organizer.lastName}
                      onChange={(e) =>
                        handleOrganizerChange(
                          index,
                          "lastName",
                          e.target.value
                        )
                      }
                      type="text"
                      placeholder={t("organizerLastName", {
                        defaultValue: "Apellido(s)",
                      })}
                      className="input-border"
                    />
                    {organizers.length > minimumOrganizers && (
                      <button
                        type="button"
                        className="text-sm text-error underline"
                        onClick={() => handleRemoveOrganizer(index)}
                      >
                        {t("removeOrganizer", {
                          defaultValue: "Eliminar organizador",
                        })}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center">
              <Button
                type="secondary"
                className="rounded-full px-4 py-2 flex items-center justify-center gap-2 text-primary text-sm font-semibold"
                onClick={handleAddOrganizer}
              >
                <FiPlus className="text-lg" />
                {t("addOrganizer", {
                  defaultValue: "Agregar organizador",
                })}
              </Button>
            </div>
          </div>
          <div className="flex flex-col space-y-2">
            <label htmlFor="slug" className="text-label font-paragraph">
              {t("slugLabel", { defaultValue: "Link personalizado" })}
            </label>
            <Input
              id="slug"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              type="text"
              placeholder={t("slugPlaceholder", {
                defaultValue: "ej. boda-juan-y-perla",
              })}
              className="input-border"
            />
            {slugHelperText && (
              <p
                className={`text-sm ${
                  slugStatus === "unavailable" || slugStatus === "invalid"
                    ? "text-error"
                    : "text-gray-500"
                }`}
              >
                {slugHelperText}
              </p>
            )}
          </div>
          <NavigationButtons
            continueLabel={t("continueButton")}
            backLabel={t("backButton", { defaultValue: "Regresar" })}
            onContinue={handleContinue}
            onBack={handleBack}
            className="w-full"
          />
        </div>
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(CreateGiftTableStep2));
